import { envConfig, isAppModeProd } from "../../config/envConfig";

import { AppUser } from "../../lib/auth";
import posthog from "posthog-js";

const getAccount = (user: AppUser) => ({
  accountId: user.account.accountId,
  name: user.account.name,
  accountType: user.account.accountType,
  updatedTime: user.account.updatedTime,
});

/**
 * Initializes Posthog.
 * This is called on app startup to initialize Posthog.
 *
 * NOTE: We opt out of event capturing in non-production environments.
 * We originally followed this guide: https://posthog.com/tutorials/multiple-environments#conditional-initialization
 * But `ph.set_config` was removed from the types around v1.324.1.
 * While researching that change I found that there is top level config for: `disable_session_recording` and `opt_out_capturing_by_default`
 * https://posthog.com/docs/session-replay/how-to-control-which-sessions-you-record
 * https://posthog.com/docs/libraries/js#opt-out-of-data-capture
 */
export const initializePosthog = (): typeof posthog => {
  posthog.init(envConfig.posthogKey, {
    api_host: envConfig.posthogHost,
    person_profiles: "identified_only",
    capture_pageview: false,
    disable_session_recording: !isAppModeProd(),
    opt_out_capturing_by_default: !isAppModeProd(),
  });

  return posthog;
};

/**
 * Sets the user to Posthog including any additional info like account details.
 */
export const setPosthogUser = (user: AppUser) => {
  const account = getAccount(user);
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");

  posthog.identify(user.userId.toString(), {
    name,
    email: user.userInfo.email,
    isSysAdmin: user.isSysAdmin,
    isInternal: user.isInternal,
    isImpersonated: user.isImpersonated,
    dateRegistered: user.userInfo.dateRegistered,
    account: account,
  });

  // posthog groups are an upgraded subscription feature
  posthog.group("company", account.accountId.toString(), account);
};

/**
 * Expected to be called when the user logs out.
 * https://posthog.com/docs/libraries/js#reset-after-logout
 */
export const logoutPosthogUser = () => {
  posthog.reset();
};

/**
 * Records a $pageview to Posthog for tracking.
 *
 * The optional `properties` argument can be used to pass additional properties to posthog.capture.
 * The main use case is to track the current tab associated to the URL.
 */
export const trackPageView = () => {
  if (posthog) {
    posthog.capture("$pageview", { $current_url: location.href });
  }
};
